import { useState } from 'react'
import { getDebugLog, clearDebugLog } from '../../../lib/debugLog'

export default function DebugLogPage() {
  const [entries, setEntries] = useState(() => getDebugLog())
  const [filter, setFilter] = useState('')
  const [shareStatus, setShareStatus] = useState<'idle' | 'ok' | 'err'>('idle')

  const filtered = filter
    ? entries.filter(e =>
        e.tag.toLowerCase().includes(filter.toLowerCase()) ||
        e.msg.toLowerCase().includes(filter.toLowerCase())
      )
    : entries

  function refresh() {
    setEntries(getDebugLog())
  }

  function handleClear() {
    clearDebugLog()
    setEntries([])
  }

  function buildText() {
    return entries
      .map(e => `[${e.ts}] [${e.tag}] ${e.msg}${e.data !== undefined ? ' ' + JSON.stringify(e.data) : ''}`)
      .join('\n')
  }

  async function shareAll() {
    const text = buildText()
    if (!text) return
    try {
      if (navigator.share) {
        await navigator.share({ title: 'InsuranceCRM Debug Log', text })
        setShareStatus('ok')
      } else {
        await navigator.clipboard.writeText(text)
        setShareStatus('ok')
      }
    } catch {
      setShareStatus('err')
    }
    setTimeout(() => setShareStatus('idle'), 2000)
  }

  function copyAll() {
    navigator.clipboard?.writeText(buildText()).catch(() => {})
  }

  return (
    <div style={{ padding: '16px', fontFamily: 'monospace', fontSize: '12px' }}>
      <div className="section-header" style={{ marginBottom: '12px' }}>
        <h1 className="page-title">Debug Log</h1>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={refresh}>Refresh</button>
          <button className="btn-primary" onClick={shareAll}>
            {shareStatus === 'ok' ? 'Shared ✓' : shareStatus === 'err' ? 'Failed' : 'Share'}
          </button>
          <button className="btn-secondary" onClick={copyAll}>Copy</button>
          <button className="btn-secondary" onClick={handleClear} style={{ color: 'var(--danger)' }}>Clear</button>
        </div>
      </div>

      <input
        className="input"
        placeholder="Filter by tag or message…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={{ marginBottom: '12px', width: '100%', boxSizing: 'border-box' }}
      />

      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
        {filtered.length} of {entries.length} entries
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">No log entries yet. Make a call to start recording diagnostics.</div>
      )}

      {filtered.map((entry, i) => (
        <div key={i} style={{
          borderBottom: '1px solid var(--border)',
          padding: '6px 0',
          wordBreak: 'break-all',
        }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>{entry.ts}</div>
          <div>
            <span style={{
              background: 'var(--brand-primary)',
              color: '#fff',
              borderRadius: '3px',
              padding: '1px 5px',
              marginRight: '6px',
              fontSize: '10px',
            }}>{entry.tag}</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{entry.msg}</span>
          </div>
          {entry.data !== undefined && (
            <pre style={{
              margin: '4px 0 0 0',
              color: 'var(--text-secondary)',
              whiteSpace: 'pre-wrap',
              fontSize: '10px',
            }}>{JSON.stringify(entry.data, null, 2)}</pre>
          )}
        </div>
      ))}
    </div>
  )
}
